import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  DollarSign,
  Ticket,
  Calendar,
  Users,
  ArrowUpRight
} from 'lucide-react';

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;

  @media (max-width: 640px) {
    gap: 1.25rem;
  }
`;

const KPISection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
  }
`;

const KPICard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
`;

const KPIHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;

  .icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.8);
  }

  .trend {
    font-size: 0.85rem;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(52, 199, 89, 0.15);
    color: #34c759;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  @media (max-width: 600px) {
    .icon-wrapper {
      width: 36px;
      height: 36px;
    }

    .trend {
      font-size: 0.8rem;
    }
  }
`;

const KPILabel = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.6);

  @media (max-width: 600px) {
    font-size: 0.85rem;
  }
`;

const KPIValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.6rem;
  }
`;

const MainContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 600px) {
    padding: 1.1rem;
    gap: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: #fff;
    font-weight: 600;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;

    h3 {
      font-size: 1.05rem;
    }
  }
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const ChartRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .header {
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    gap: 0.5rem;
    flex-wrap: wrap;
    word-break: break-word;
  }

  .bar-bg {
    height: 8px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #4f46e5, #0ea5e9);
    border-radius: 4px;
  }

  .meta {
    display: flex;
    justify-content: space-between;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.4);
    gap: 0.25rem;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  @media (max-width: 600px) {
    gap: 0.75rem;

    .header {
      flex-direction: column;
      align-items: flex-start;
    }

    .meta {
      flex-direction: column;
      gap: 0.15rem;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;

  th {
    text-align: left;
    padding: 1rem;
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  td {
    padding: 1rem;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.95rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    &.amount {
      font-weight: 600;
      color: #fff;
    }

    &.secondary {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  @media (max-width: 768px) {
    min-width: 0;
  }

  @media (max-width: 640px) {
    border: none;

    thead {
      display: none;
    }

    tbody {
      display: grid;
      gap: 0.75rem;
    }

    tr {
      display: grid;
      gap: 0.5rem;
      padding: 0.85rem 0.9rem;
      border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.06);
      background: rgba(255, 255, 255, 0.03);
    }

    td {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.35rem;
      padding: 0;
      font-size: 0.9rem;
      border: none;
    }

    td::before {
      content: attr(data-label);
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.45);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    td.amount {
      font-size: 1rem;
    }
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 600px) {
    margin: 0 -0.25rem;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 600px) {
    gap: 0.75rem;
  }
`;

const ActivityItem = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.5rem 0;

  @media (max-width: 600px) {
    gap: 0.75rem;
    align-items: flex-start;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.75);

  @media (max-width: 600px) {
    width: 36px;
    height: 36px;
  }
`;

const ActivityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  .title {
    font-size: 0.9rem;
    color: #fff;
  }

  .subtitle {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.4);
  }

  @media (max-width: 600px) {
    .title {
      font-size: 0.85rem;
    }

    .subtitle {
      font-size: 0.75rem;
    }
  }
`;

const AnalyticsDashboard = ({ summary, events, formatCurrency, formatNumber }) => {
  const defaults = {
    totalRevenue: 0,
    ticketsSold: 0,
    totalEvents: 0,
    activeEvents: 0,
    avgOccupancy: 0
  };

  const data = { ...defaults, ...summary };
  const avgOccupancy = data.averageOccupancy || data.avgOccupancy || 0;

  // Ensure formatters
  const fCurrency = formatCurrency || ((v) => `$${v}`);
  const fNumber = formatNumber || ((v) => v);

  const kpiData = [
    {
      label: 'Total Revenue',
      value: fCurrency(data.totalRevenue),
      icon: <DollarSign size={20} />,
      color: '#10b981',
      trend: '+12.5%' // Mock trend
    },
    {
      label: 'Tickets Sold',
      value: fNumber(data.ticketsSold),
      icon: <Ticket size={20} />,
      color: '#3b82f6',
      trend: '+8.2%'
    },
    {
      label: 'Active Events',
      value: `${fNumber(data.activeEvents)} / ${fNumber(data.totalEvents)}`,
      icon: <Calendar size={20} />,
      color: '#f59e0b',
      trend: null
    },
    {
      label: 'Avg. Occupancy',
      value: `${avgOccupancy.toFixed(1)}%`,
      icon: <Users size={20} />,
      color: '#8b5cf6',
      trend: '+2.1%'
    }
  ];

  // Prepare top performing events for chart
  const sortedByRevenue = [...(summary?.events || events || [])]
    .sort((a, b) => (Number(b.revenue) || 0) - (Number(a.revenue) || 0))
    .slice(0, 5);

  const maxRevenue = Math.max(...sortedByRevenue.map(e => Number(e.revenue) || 0), 1);

  return (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <KPISection>
        {kpiData.map((kpi, idx) => (
          <KPICard
            key={kpi.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
          >
            <KPIHeader>
              <div className="icon-wrapper" style={{ color: kpi.color, background: `${kpi.color}15` }}>
                {kpi.icon}
              </div>
              {kpi.trend && (
                <div className="trend">
                  <ArrowUpRight size={14} />
                  {kpi.trend}
                </div>
              )}
            </KPIHeader>
            <KPILabel>{kpi.label}</KPILabel>
            <KPIValue>{kpi.value}</KPIValue>
          </KPICard>
        ))}
      </KPISection>

      <MainContentGrid>
        <Section>
          <SectionHeader>
            <h3>Top Performing Events by Revenue</h3>
          </SectionHeader>
          <ChartContainer>
            {sortedByRevenue.length > 0 ? (
              sortedByRevenue.map((evt) => {
                const revenue = Number(evt.revenue) || 0;
                const percentage = (revenue / maxRevenue) * 100;
                return (
                  <ChartRow key={evt.eventId || evt.id}>
                    <div className="header">
                      <span>{evt.title}</span>
                      <span>{fCurrency(revenue)}</span>
                    </div>
                    <div className="bar-bg">
                      <motion.div
                        className="bar-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                      />
                    </div>
                    <div className="meta">
                      <span>{fNumber(evt.ticketsSold)} tickets sold</span>
                      <span>{evt.occupancy ? `${evt.occupancy}% occupancy` : ''}</span>
                    </div>
                  </ChartRow>
                );
              })
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                No sales data available yet
              </div>
            )}
          </ChartContainer>
        </Section>

        <Section>
          <SectionHeader>
            <h3>Recent Activity</h3>
          </SectionHeader>
          <ActivityList>
            {/* Mock activity feed since we don't have real granular activity data in analytics summary yet */}
            <ActivityItem>
              <ActivityIcon style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                <Ticket size={16} />
              </ActivityIcon>
              <ActivityText>
                <div className="title">Ticket Sales Spiked</div>
                <div className="subtitle">High volume detected for "Summer festival"</div>
              </ActivityText>
            </ActivityItem>

            <ActivityItem>
              <ActivityIcon style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                <DollarSign size={16} />
              </ActivityIcon>
              <ActivityText>
                <div className="title">Revenue Milestone</div>
                <div className="subtitle">Daily revenue exceeded $5k target</div>
              </ActivityText>
            </ActivityItem>

            <ActivityItem>
              <ActivityIcon style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                <Users size={16} />
              </ActivityIcon>
              <ActivityText>
                <div className="title">User Growth</div>
                <div className="subtitle">+15 new signups today</div>
              </ActivityText>
            </ActivityItem>
          </ActivityList>
        </Section>
      </MainContentGrid>

      <Section>
        <SectionHeader>
          <h3>Event Performance Overview</h3>
        </SectionHeader>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Event Name</th>
                <th>Status</th>
                <th>Tickets Sold</th>
                <th>Occupancy</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {((summary?.events || events || []).length > 0) ? (
                (summary?.events || events || []).map(evt => (
                  <tr key={evt.eventId || evt.id}>
                    <td data-label="Event Name">
                      <div style={{ fontWeight: 500 }}>{evt.title}</div>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                        {evt.venueName || 'TBD'} • {evt.startTime ? new Date(evt.startTime).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td data-label="Status">
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.8)'
                      }}>
                        ACTIVE
                      </span>
                    </td>
                    <td className="secondary" data-label="Tickets Sold">{fNumber(evt.ticketsSold)}</td>
                    <td data-label="Occupancy">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', minWidth: '60px' }}>
                          <div style={{ width: `${evt.occupancy || 0}%`, height: '100%', background: '#3b82f6', borderRadius: '2px' }} />
                        </div>
                        <span style={{ minWidth: '32px', textAlign: 'right', fontSize: '0.85rem' }}>{Math.round(evt.occupancy || 0)}%</span>
                      </div>
                    </td>
                    <td className="amount" data-label="Revenue">{fCurrency(evt.revenue || 0)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.3)' }}>
                    No events found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </Section>
    </Container>
  );
};

export default AnalyticsDashboard;
